/**
 * @file Provides methods to interact with learning path data
 * @author Anna Kurylo <akurylo@redhat.com>
 */

import axios from 'axios';
import { LEARNING_PATH_API_URL } from '../config/constants';
import type { LearningPath } from '../types';

const { get, post, put } = axios;

export type LearningPathsResponse = {
  items: LearningPath[];
};

export const getLearningPaths = async (): Promise<LearningPath[]> => {
  try {
    const res = await get<LearningPathsResponse>(LEARNING_PATH_API_URL);
    return res.data?.items ?? [];
  } catch (error) {
    throw new Error(String(error));
  }
};

export const getJoinedLearningPaths = async (): Promise<LearningPath[]> => {
  try {
    const res = await get<LearningPathsResponse>(
      `${LEARNING_PATH_API_URL}?joined=true`,
    );
    return res.data?.items ?? [];
  } catch {
    return [];
  }
};

export const joinLearningPath = async (slug: string): Promise<LearningPath> => {
  const res = await post<LearningPath>(`${LEARNING_PATH_API_URL}${slug}/join/`);
  return res.data;
};

export const unjoinLearningPath = async (
  slug: string,
): Promise<LearningPath> => {
  const res = await post<LearningPath>(
    `${LEARNING_PATH_API_URL}${slug}/unjoin/`,
  );
  return res.data;
};

export const getLearningPath = async (slug: string): Promise<LearningPath> => {
  const res = await get<LearningPath>(`${LEARNING_PATH_API_URL}${slug}/`);
  return res.data;
};

export const createCustomSkillPath = async (
  skillPathData: Partial<LearningPath>,
): Promise<LearningPath> => {
  const res = await post<LearningPath>(
    `${LEARNING_PATH_API_URL}create_custom`,
    skillPathData,
  );
  return res.data;
};

export const updateCustomSkillPath = async (
  skillPathData: Partial<LearningPath>,
  id: string,
): Promise<LearningPath> => {
  const res = await put<LearningPath>(
    `${LEARNING_PATH_API_URL}${id}/update_custom`,
    skillPathData,
  );
  return res.data;
};

export const getCustomSkillPath = async (id: string): Promise<LearningPath> => {
  const res = await get<LearningPath>(
    `${LEARNING_PATH_API_URL}${id}/get_custom`,
  );
  return res.data;
};

export const deleteCustomSkillPath = async (id: string): Promise<void> => {
  await put(`${LEARNING_PATH_API_URL}${id}/delete_custom`);
};
